import { forwardRef } from "react";
import { cn } from "../lib/cn";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FirstPageIcon,
  LastPageIcon,
} from "../lib/icons";

/**
 * Pagination — accessible page navigation, MUI Pagination semantics.
 *
 *   <Pagination page={page} count={12} onChange={setPage} />
 *
 * `page` is 1-based and `count` is the total number of pages. Renders
 * first/prev/pages-with-ellipses/next/last with compact ghost buttons;
 * the first/prev and next/last pairs disable at the bounds.
 */

/** Inclusive numeric range; empty when start is past end. */
function range(start: number, end: number): number[] {
  const length = end - start + 1;
  return Array.from({ length }, (_, index) => start + index);
}

type PageItem = number | "ellipsis-start" | "ellipsis-end";

/** Port of MUI usePagination's item model (boundaryCount fixed at 1). */
function buildPageItems(
  page: number,
  count: number,
  siblingCount: number,
): PageItem[] {
  const startPages = range(1, Math.min(1, count));
  const endPages = range(Math.max(count, 2), count);

  const siblingsStart = Math.max(
    Math.min(page - siblingCount, count - siblingCount * 2 - 2),
    3,
  );
  const siblingsEnd = Math.min(
    Math.max(page + siblingCount, siblingCount * 2 + 3),
    endPages.length > 0 ? endPages[0] - 2 : count - 1,
  );

  return [
    ...startPages,
    ...(siblingsStart > 3
      ? ["ellipsis-start" as const]
      : 2 < count - 1
        ? [2]
        : []),
    ...range(siblingsStart, siblingsEnd),
    ...(siblingsEnd < count - 2
      ? ["ellipsis-end" as const]
      : count - 1 > 1
        ? [count - 1]
        : []),
    ...endPages,
  ];
}

const pageButtonClasses =
  "inline-flex h-8 min-w-8 select-none items-center justify-center rounded-md px-1 text-sm outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-border-focused focus-visible:ring-offset-1 focus-visible:ring-offset-surface-canvas disabled:pointer-events-none disabled:text-content-disabled";

interface PaginationPageButtonProps {
  page: number;
  selected: boolean;
  onSelect: (page: number) => void;
}

function PaginationPageButton({
  page,
  selected,
  onSelect,
}: PaginationPageButtonProps) {
  return (
    <li>
      <button
        type="button"
        aria-current={selected ? "page" : undefined}
        aria-label={`Go to page ${page}`}
        onClick={() => {
          onSelect(page);
        }}
        className={cn(
          pageButtonClasses,
          selected
            ? "bg-surface-selected font-medium text-content-primary"
            : "text-content-primary hover:bg-surface-raised active:bg-surface-pressed",
        )}
      >
        {page}
      </button>
    </li>
  );
}

interface PaginationNavButtonProps {
  label: string;
  disabled: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}

function PaginationNavButton({
  label,
  disabled,
  onSelect,
  children,
}: PaginationNavButtonProps) {
  return (
    <li>
      <button
        type="button"
        aria-label={label}
        disabled={disabled}
        onClick={onSelect}
        className={cn(
          pageButtonClasses,
          "text-content-secondary hover:bg-surface-raised hover:text-content-primary active:bg-surface-pressed",
        )}
      >
        {children}
      </button>
    </li>
  );
}

function PaginationEllipsis() {
  return (
    <li
      aria-hidden="true"
      className="flex h-8 min-w-8 select-none items-center justify-center px-1 text-sm text-content-disabled"
    >
      …
    </li>
  );
}

export interface PaginationProps {
  /** Current page, 1-based. */
  page: number;
  /** Total number of pages. */
  count: number;
  onChange: (page: number) => void;
  /** Pages kept either side of the current page (default 1). */
  siblingCount?: number;
  className?: string;
}

/**
 * nav > ul > li structure. Page buttons report via `onChange`; nav arrows
 * are disabled at the bounds rather than hidden.
 */
export const Pagination = forwardRef<HTMLElement, PaginationProps>(
  ({ page, count, onChange, siblingCount = 1, className }, ref) => {
    const iconSizing = "size-4";
    const items = count > 0 ? buildPageItems(page, count, siblingCount) : [];
    const atStart = page <= 1;
    const atEnd = page >= count;

    return (
      <nav ref={ref} aria-label="Pagination" className={className}>
        <ul className="flex items-center">
          <PaginationNavButton
            label="Go to first page"
            disabled={atStart}
            onSelect={() => {
              onChange(1);
            }}
          >
            <FirstPageIcon className={iconSizing} aria-hidden="true" />
          </PaginationNavButton>
          <PaginationNavButton
            label="Go to previous page"
            disabled={atStart}
            onSelect={() => {
              onChange(page - 1);
            }}
          >
            <ChevronLeftIcon className={iconSizing} aria-hidden="true" />
          </PaginationNavButton>
          {items.map((item) => {
            if (item === "ellipsis-start" || item === "ellipsis-end") {
              return <PaginationEllipsis key={item} />;
            }
            return (
              <PaginationPageButton
                key={item}
                page={item}
                selected={item === page}
                onSelect={onChange}
              />
            );
          })}
          <PaginationNavButton
            label="Go to next page"
            disabled={atEnd}
            onSelect={() => {
              onChange(page + 1);
            }}
          >
            <ChevronRightIcon className={iconSizing} aria-hidden="true" />
          </PaginationNavButton>
          <PaginationNavButton
            label="Go to last page"
            disabled={atEnd}
            onSelect={() => {
              onChange(count);
            }}
          >
            <LastPageIcon className={iconSizing} aria-hidden="true" />
          </PaginationNavButton>
        </ul>
      </nav>
    );
  },
);
Pagination.displayName = "Pagination";
